<template>
  <transition name="FADE" mode="out-in">

    <div class="flex-1-1 flex-0-1:d" :class="{ 'd-flex': $device.isMobileOrTablet }">
      <button
          v-if="isNewMatch && $device.isMobileOrTablet "
          class="PASS-BUTTON"
          :class="{ 'x-pulse': isFetching }"
          selenium="job-details-pass-button"
          :disabled="isFetching"
          @click.prevent="onPassOpen">
        <span v-if="!isFetching">{{ passButtonLabel }}</span>
      </button>
      <button
          v-if="!isNewMatch && !isClosedMatch"
          class="REQUEST-REVIEW-BUTTON"
          :class="{ 'x-pulse': isFetching }"
          @click.prevent="onRequestReview">
        <span v-if="!isFetching" class="d-flex ai-center"> Request Review </span>
      </button>
      <button
          v-if="!isNewMatch && isClosedMatch"
          class="REACTIVATE-JOB-BUTTON"
          :class="{ 'x-pulse': isFetching }"
          @click.prevent="onReactivateJob">
        <span v-if="!isFetching" class="d-flex"> Reactivate Job </span>
      </button>
      <pass-modal
          :is-open="isPassModalOpened"
          @secondary="onPassClose"
          @primary="(...args) => onAction('pass', ...args)"/>
      <button
          v-if="$device.isMobileOrTablet && !isClosedMatch"
          class="CTA-BUTTON"
          :class="{ 'x-pulse': isFetching }"
          :disabled="isFetching"
          @click.prevent="onActionModalOpen">
        <span v-if="!isFetching">{{ isNewMatch ? 'Contact' : 'Update Status' }}</span>
      </button>
      <sub-status-modal
          :is-open="SubStatusModalOpened"
          :phone="job.location.phone | phoneNumber"
          @primary="(...args) => onAction(...args)"
          @secondary="onSubStatusClose"
      />

      <action-modal
          v-if="$device.isMobileOrTablet"
          :is-open="isActionModalOpened"
          :ctas="currentCtas"
          @primary="onAction"
          @secondary="onActionModalClose" />
      <div v-else-if="!isNewMatch && !isClosedMatch">
        <vk-listbox
            class="ACTION JOB m-b-2:d"
            menuClass="ACTION-MENU"
            value=""
            :display-text="isFetching ? '' : 'Update Job Status'"
            :class="{ 'x-pulse': isFetching }"
            :disabled="isFetching"
            selenium="status-dropdown"
            @input="onStatusConfirmationOpen">
          <vk-listbox-option
              v-for="{ key, label } in currentCtas"
              :key="key"
              :value="key"
              :selenium="`status-option-${ key === 'yes' ? 'first' : 'second' }`">
            {{ label }}
          </vk-listbox-option>
        </vk-listbox>
        <vk-listbox
            v-if="isPotentialMatch"
            class="ACTION STATUS"
            menuClass="ACTION-MENU"
            :value="selectedSubStatus"
            :display-text="isFetching ? '' : 'Update Contact Status'"
            :class="{ 'x-pulse': isFetching }"
            :disabled="isFetching"
            selenium="status-dropdown"
            @input="subStatusUpdate">
          <vk-listbox-option
              v-for="{ key, text } in currentSubStatuses"
              class="bd-b-gray50"
              :key="key"
              :value="text">
            {{ text }}
          </vk-listbox-option>
        </vk-listbox>

      </div>
      <div v-if="hasError && !$device.isMobileOrTablet " class="c-red m-y-1">
        <vk-icon name="exclamation" class="ERROR-ICON "/>
        Contact Status Update Failed, please try again.
      </div>
      <status-confirmation-modal
          :is-open="isStatusConfirmationOpened"
          @secondary="onConfirmationClose"
          @primary="onConfirmation">
        <template v-if="currentCtaKey">
          <div class="d-flex">
            <div class="flex-0-0 m-r-1">
              <vk-icon name="exclamation-circle-outlined" size="24" class="INFO"/>
            </div>
            <div class="fd-column text-left">
              <div class="text-bold" >
                {{ $options.JOBS_STATUS_ALIASES[alias].ctas[currentCtaKey].confirmation }}
              </div>
              <div>
                {{ $options.JOBS_STATUS_ALIASES[alias].ctas[currentCtaKey].message }}
              </div>
            </div>

          </div>
        </template>
      </status-confirmation-modal>

    </div>
  </transition>
</template>

<script>
import jobParams from '@/mixins/job-params';
import job from '@/mixins/job';
import passEducation from '@/views/Dashboard/mixins/pass-education';
import { SUB_STATUS_OPTIONS } from '@/constants/sub-status-options';
import http from '@homeservices/api-kit/axios';
import { mapGetters, mapActions } from 'vuex';
import { addDays, isAfter } from 'date-fns';

import PassModal from './PassModal';
import ActionModal from './ActionModal';
import SubStatusModal from './SubStatusModal';
import StatusConfirmationModal from './StatusConfirmationModal';

export default {
  name: 'secondary-cta',
  SUB_STATUS_OPTIONS,
  components: {
    PassModal,
    ActionModal,
    SubStatusModal,
    StatusConfirmationModal,
  },
  mixins: [
    passEducation,
    jobParams,
    job,
  ],
  data () {
    return {
      currentCtaKey: '',
      currentParams: undefined,
      isPassModalOpened: false,
      isActionModalOpened: false,
      isRequestingReview: false,
      SubStatusModalOpened: false,
      currentSubStatuses: [],
      selectedSubStatus: '',
      hasError: false,
      isStatusConfirmationOpened: false,
    };
  },
  computed: {
    ...mapGetters('account/pro/info', [
      'serviceProviderId',
    ]),
    ...mapGetters('ui', ['subStatuses']),
    isOverdue () {
      return isAfter(new Date(Date.now()), addDays(new Date(this.timeToExpire), 2))
        && this.$options.JOBS_STATUS_ALIASES.new.statuses.includes(this.match.status);
    },
    passButtonLabel () {
      return !this.isOverdue ? 'Pass & Refund' : this.$options.JOBS_STATUS_ALIASES.new.ctas.pass.label;
    },
    isFetching () {
      return this.currentJobMeta.status === this.$options.REQUEST_LIFECYCLE.FETCHING;
    },
    currentCtas () {
      const status = this.isSearchAlias ? this.match.status : this.jobsStatusAlias;
      return Object.values(this.$options.JOBS_STATUS_ALIASES[status].ctas || {})
        .filter(({ key }) => key !== 'pass');
    },
    alias () {
      const { POTENTIAL, HIRED, CLOSED } = this.$options.MATCH_STATUSES;
      const isPotential = (this.jobsStatusAlias === POTENTIAL || this.match.status === POTENTIAL);
      if (this.isRequestingReview && isPotential) {
        return HIRED;
      }
      if (this.match.status === CLOSED) {
        return CLOSED;
      }
      return this.isSearchAlias ? this.match.status : this.jobsStatusAlias;
    },
    isSearchAlias () {
      return this.jobsStatusAlias === 'search';
    },
  },
  watch: {
    subStatuses: {
      handler (newVal, oldVal) {
        if (newVal !== oldVal) {
          this.currentSubStatuses = newVal;
        }
      },
      immediate: true, // This ensures the watcher runs immediately on component mount
      deep: true, // Use deep watch if subStatuses is an array of objects
    },
  },
  async mounted () {
    await this.getContactSubStatuses();
  },
  methods: {
    ...mapActions('ui', ['getContactSubStatuses']),

    onRequestReview () {
      this.isRequestingReview = true;
      this.onStatusConfirmationOpen('yes');
    },
    onReactivateJob () {
      this.onAction('reactivate');
    },
    onConfirmation () {
      if (this.currentCtaKey === 'pass') {
        this.setPreviouslyPassed();
      }

      this.isStatusConfirmationOpened = false;

      this.onAction(this.currentCtaKey, this.currentParams);
    },
    onConfirmationClose () {
      this.isStatusConfirmationOpened = false;
      this.currentCtaKey = '';
      this.currentParams = undefined;
      this.isRequestingReview = false;
    },
    onPassOpen () {
      this.isPassModalOpened = true;
    },
    onPassClose () {
      this.isPassModalOpened = false;
    },
    onActionModalOpen () {
      this.isActionModalOpened = true;
    },
    onStatusConfirmationOpen (key) {
      this.currentCtaKey = key;
      this.isStatusConfirmationOpened = true;
    },
    async onAction (ctaKey, params) {
      this.isActionModalOpened = false;
      if (ctaKey === 'call') {
        await this.subStatusUpdate(
          this.$options.SUB_STATUS_OPTIONS.selected_show_number.text, true
        );
      }

      if (ctaKey === 'pass') {
        this.isPassModalOpened = false;
      }

      if (ctaKey === 'message') {
        this.$router.replace({
          name: 'dashboard::pro::jobs',
          params: this.$route.params,
          query: {
            ...this.$route.query,
            feature: 'messages',
          },
        });

        return;
      }

      const {
        action,
        from,
        to,
        confirmation,
      } = this.$options.JOBS_STATUS_ALIASES[this.alias].ctas[ctaKey];

      if (confirmation && !this.isStatusConfirmationOpened && !this.currentCtaKey) {
        this.isStatusConfirmationOpened = true;
        this.currentCtaKey = ctaKey;
        this.currentParams = params;

        if (this.isStatusConfirmationOpened) {
          return;
        }
      }

      this.isStatusConfirmationOpened = false;
      this.currentCtaKey = '';
      this.currentParams = undefined;
      this.isRequestingReview = false;

      await this.onStatusUpdate({
        action,
        from,
        to,
        params,
        order_hash: this.job.order_hash,
      });

      if (action === this.$options.MATCH_STATUS_ACTIONS.CALL_CONSUMER) {
        this.resetBusyPassCount();
      }

      if (action === this.$options.MATCH_STATUS_ACTIONS.REJECT) {
        this.incrementCounters({ reason: params.reason });
      }
    },
    onActionModalClose () {
      this.isActionModalOpened = false;
    },
    async onStatusUpdate (payload) {
      await this.transitionJob(payload);

      const isPastMatchStatus = payload.to === this.$options.JOBS_STATUS_ALIASES.past.key;

      this.$fireToast({
        message: this.$options.getMatchStatusActionsMessage({
          action: payload.action,
          firstName: this.job.location.first_name,
        }),
      });

      // To prevent screen animation to the next header status when
      // updating the status while on the search screan.
      if (this.isSearchAlias) {
        return;
      }

      // If the user changed status towards active status –
      // we are changing jobsStatusAlias to the next status and we are also respecting jobId.
      // If the user changed status towards past status –
      // we don't want to navigate there unless it has unsaved changes.
      // Instead, we are navigating to the same jobsStatusAlias without jobId,
      // so fallback which is implemented for JobsList will automatically select the next job
      // (e.g. /dashboard/new redirects to /dashboard/new/<first_id_in_a_list>
      this.$router.replace({
        name: 'dashboard::pro::jobs',
        params: {
          jobsStatusAlias: isPastMatchStatus ? payload.from : payload.to,
          jobId: isPastMatchStatus ? undefined : payload.order_hash,
        },
        query: this.$route.query,
        // When job url transition is aborted but job status has already changed to past status -
        // we need to navigate there, so user won't lose its progress. See replace func signature
        // in VueRouter docs.
      }, () => null, () => this.$router.replace({
        name: 'dashboard::pro::jobs',
        params: {
          jobsStatusAlias: payload.to,
          jobId: payload.order_hash,
        },
        query: this.$route.query,
      }));

      this.$events.$emit(this.$events.PRO_CHANGE_STATUS, {
        jobID: payload.order_hash,
        changeTo: payload.action,
      });

      if (payload.action === this.$options.MATCH_STATUS_ACTIONS.CALL_CONSUMER) {
        this.$events.$emit(this.$events.PRO_CALL, {
          jobID: payload.order_hash,
        });

        return;
      }
      if (payload.action === this.$options.MATCH_STATUS_ACTIONS.REACTIVATE) {
        this.$events.$emit(this.$events.PRO_JOB_REACTIVATE, {
          jobID: payload.order_hash,
          changeTo: payload.action,
        });

        return;
      }

      if (payload.action === this.$options.MATCH_STATUS_ACTIONS.REJECT) {
        this.$events.$emit(this.$events.PRO_JOB_REJECT, {
          jobID: payload.jobId,
          reason: payload.reason,
          other: payload.explanation,
        });
      }
    },
    onSubStatusModalOpen () {
      this.SubStatusModalOpened = true;
    },
    onSubStatusClose () {
      this.SubStatusModalOpened = false;
    },
    async subStatusUpdate (selectedSubStatus, isInitial) {
      const eventToEmit = isInitial
        ? this.$events.SUB_STATUS_INITIAL
        : this.$events.SUB_STATUS_UPDATE;
      this.hasError = false;
      try {
        await http.put(`/platform/workorder/r/workorder/service-provider-sub-status/v1?order_hash=${this.job.order_hash}&service_provider_id=${this.serviceProviderId}&service_provider_status=potential&service_provider_sub_status=${selectedSubStatus}`);
        this.$events.$emit(eventToEmit, { substatus: selectedSubStatus });
        this.$emit('primary', 'call', { hasSubStatus: selectedSubStatus });
      } catch {
        this.hasError = true;
      } finally {
        this.$emit('update');
      }
    },
    toggleReasonModal () {
      this.isReasonModalOpened = false;
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "~@homeservices/design-kit/tools";

  @import "~@/views/Dashboard/constants";

  .PASS-BUTTON {
    @include button-primary;
    @include button-small;
    @include margin(2 2 0 0, $md: 2 0 0, $lg: 0);
    @include responsive(min-width, auto, $md: 86px);
    @include responsive(flex, 1 1 50%, $md: 0 1 auto);
    display: flex;
    font-weight: 600;
  }
  .STATUS /deep/.VK-LISTBOX-TEXT {
    &:before {
      content: url("data:image/svg+xml,%3Csvg width='16' height='19' viewBox='0 0 16 19' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M15.4531 5.83691C15.6641 5.83691 15.875 5.66113 15.875 5.41504V4.00879C15.875 3.79785 15.6641 3.58691 15.4531 3.58691H14.75V1.89941C14.75 0.985352 13.9766 0.211914 13.0625 0.211914H1.8125C0.863281 0.211914 0.125 0.985352 0.125 1.89941V16.5244C0.125 17.4736 0.863281 18.2119 1.8125 18.2119H13.0625C13.9766 18.2119 14.75 17.4736 14.75 16.5244V14.8369H15.4531C15.6641 14.8369 15.875 14.6611 15.875 14.415V13.0088C15.875 12.7979 15.6641 12.5869 15.4531 12.5869H14.75V10.3369H15.4531C15.6641 10.3369 15.875 10.1611 15.875 9.91504V8.50879C15.875 8.29785 15.6641 8.08691 15.4531 8.08691H14.75V5.83691H15.4531ZM7.4375 4.71191C8.66797 4.71191 9.6875 5.73145 9.6875 6.96191C9.6875 8.22754 8.66797 9.21191 7.4375 9.21191C6.17188 9.21191 5.1875 8.22754 5.1875 6.96191C5.1875 5.73145 6.17188 4.71191 7.4375 4.71191ZM11.375 13.0439C11.375 13.4307 11.0234 13.7119 10.5664 13.7119H4.27344C3.85156 13.7119 3.5 13.4307 3.5 13.0439V12.376C3.5 11.251 4.55469 10.3369 5.85547 10.3369H6.03125C6.45312 10.5479 6.91016 10.6182 7.4375 10.6182C7.92969 10.6182 8.38672 10.5479 8.80859 10.3369H8.98438C10.2852 10.3369 11.375 11.251 11.375 12.376V13.0439Z' fill='%23F96302'/%3E%3C/svg%3E%0A");
    }
  }
   .JOB /deep/.VK-LISTBOX-TEXT {
    &:before {
    content: url("data:image/svg+xml,%3Csvg width='19' height='16' viewBox='0 0 19 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11.3264 10.6875C11.3264 11.0039 11.0452 11.25 10.7639 11.25H7.38892C7.07251 11.25 6.82642 11.0039 6.82642 10.6875V9H0.076416V14.0625C0.076416 14.9766 0.849854 15.75 1.76392 15.75H16.3889C17.2678 15.75 18.0764 14.9766 18.0764 14.0625V9H11.3264V10.6875ZM16.3889 3.375H13.5764V1.6875C13.5764 0.808594 12.7678 0 11.8889 0H6.26392C5.34985 0 4.57642 0.808594 4.57642 1.6875V3.375H1.76392C0.849854 3.375 0.076416 4.18359 0.076416 5.0625V7.875H18.0764V5.0625C18.0764 4.18359 17.2678 3.375 16.3889 3.375ZM11.3264 3.375H6.82642V2.25H11.3264V3.375Z' fill='%23F96302'/%3E%3C/svg%3E%0A");
  }}

.ERROR-ICON {
  top: 4px;
  position: relative;
  line-height: 18px;
  padding: 4px;
  font-size: 12px;
  background-color: $color-red;
  color: white;
}
  .ACTION {
    @include margin-top(0, $md: 2, $lg: 0);
    width: 300px;

    &.x-pulse {
      /deep/ .CHEVRON {
        display: none;
      }
    }

    /deep/ .ACTION-MENU {
      height: auto;
    }
  }

  .CTA-BUTTON {
    @include button-primary;
    @include button-small;
    @include responsive(width, auto, $md: 14*$unit);
    @include responsive(flex, 1 1 50%, $md: 0 1 auto);
    margin-top: 2*$unit;
    display: flex;
    font-weight: 600;
  }

  .REACTIVATE-JOB-BUTTON {
    min-width: 300px;
    @include button-primary;
    @include button-small;
    @include responsive(width, auto, $md: 100%);
    @include responsive(flex, 1 1 50%, $md: 0 1 auto);
    @include margin(2 0 0 0, $md: 2 0 0, $lg: 0 0 2);
    font-weight: 600;
  }

  .REQUEST-REVIEW-BUTTON {
    @include button-primary;
    @include button-small;
    @include responsive(width, auto, $md: 100%);
    @include responsive(flex, 1 1 50%, $md: 0 1 auto);
    @include margin(2 2 0 0, $md: 2 0 0, $lg: 0 0 2);
    font-weight: 600;
    max-width: 300px;
  }
  .INFO {
    color: #1890FF;
  }
</style>
